import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Mail, Send, Loader2, Clock, AlertOctagon, ShieldCheck, TrendingUp, Users, Briefcase } from "lucide-react";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKKPICard, VLKButton, VLKInput, VLKSelect, VLKAlertDialog, VLKLabel,
  VLKRiskMeter, toast, VLKEmptyState,
} from "@/components/vlk";

const PERIOD_OPTIONS = [
  { value: "daily", label: "Daily · last 24h" },
  { value: "weekly", label: "Weekly · last 7d" },
];

export default function Digest() {
  const [period, setPeriod] = useState("daily");
  const [showSend, setShowSend] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["digest", "preview", period],
    queryFn: () => api.get("/digest/preview", { params: { period } }).then((r) => r.data),
  });

  const { data: snaps, refetch: refetchSnaps } = useQuery({
    queryKey: ["digest", "snapshots"],
    queryFn: () => api.get("/digest/snapshots").then((r) => r.data),
  });

  const kpis = data?.kpis;

  return (
    <div data-testid="digest-page">
      <VLKPageHeader
        title="Executive digest"
        description="Distilled operational posture for stakeholders. Preview live or queue a snapshot for regulator recall. Email delivery is stubbed pending SMTP integration."
        actions={
          <div className="flex items-center gap-2">
            <VLKSelect data-testid="digest-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="min-w-[200px]">
              {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </VLKSelect>
            <VLKButton size="md" data-testid="digest-send" onClick={() => setShowSend(true)}>
              <Send className="h-4 w-4" /> Queue send
            </VLKButton>
          </div>
        }
      />

      {isLoading || !kpis ? (
        <div className="p-10 flex items-center justify-center text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Compiling digest…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
            <VLKKPICard testId="digest-kpi-identities" label="Total identities" value={kpis.total_identities} icon={ShieldCheck} />
            <VLKKPICard testId="digest-kpi-locked" label="Locked" value={kpis.locked_identities} tone="danger" icon={AlertOctagon} />
            <VLKKPICard testId="digest-kpi-recoveries" label="Recoveries submitted" value={kpis.recoveries_submitted} icon={Users} />
            <VLKKPICard testId="digest-kpi-recoveries-done" label="Reactivated" value={kpis.recoveries_reactivated} tone="success" icon={TrendingUp} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <VLKKPICard testId="digest-kpi-audit" label="Audit events" value={kpis.audit_events.toLocaleString()} icon={Clock} />
            <VLKKPICard testId="digest-kpi-critical" label="Critical events" value={kpis.critical_events} tone="danger" icon={AlertOctagon} />
            <VLKKPICard testId="digest-kpi-cases" label="Open cases" value={kpis.open_cases} tone="warning" icon={Briefcase} />
            <VLKKPICard testId="digest-kpi-crit-cases" label="Critical cases" value={kpis.critical_active_cases} tone="danger" icon={Briefcase} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <VLKCard>
              <div className="px-5 py-4 border-b border-border">
                <div className="font-display text-sm">Top actions</div>
                <div className="text-xs text-muted-foreground mt-0.5">Most-frequent audit actions in window.</div>
              </div>
              <div className="p-5">
                {data.top_actions.length === 0 ? (
                  <div className="text-sm text-muted-foreground">No activity yet.</div>
                ) : (
                  <ul className="divide-y divide-border">
                    {data.top_actions.map((a) => (
                      <li key={a.action} className="py-2 flex items-center justify-between text-sm">
                        <span className="font-mono">{a.action}</span>
                        <span className="font-mono text-muted-foreground tabular">{a.count.toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </VLKCard>

            <VLKCard>
              <div className="px-5 py-4 border-b border-border">
                <div className="font-display text-sm">Critical & high events</div>
                <div className="text-xs text-muted-foreground mt-0.5">Latest 10 elevated-risk audit entries.</div>
              </div>
              <div className="p-0">
                {data.critical_events.length === 0 ? (
                  <div className="p-5 text-sm text-muted-foreground">Nothing elevated in window. 👍</div>
                ) : (
                  <ul className="divide-y divide-border max-h-[320px] overflow-auto">
                    {data.critical_events.map((e) => (
                      <li key={e.ledger_id} className="p-3 text-xs font-mono flex items-start gap-3">
                        <VLKRiskMeter level={e.risk_level} />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{e.action}</div>
                          <div className="text-muted-foreground truncate">{e.actor_email} · {e.target_id || "—"}</div>
                        </div>
                        <div className="text-muted-foreground tabular text-right shrink-0">{new Date(e.timestamp).toLocaleString()}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </VLKCard>
          </div>

          <VLKCard className="mt-4">
            <div className="px-5 py-4 border-b border-border">
              <div className="font-display text-sm">Queued snapshots</div>
              <div className="text-xs text-muted-foreground mt-0.5">Digest snapshots persisted for regulator recall.</div>
            </div>
            <div className="p-5">
              {!snaps?.items?.length ? (
                <div className="text-sm text-muted-foreground">No snapshots yet.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {snaps.items.map((s) => (
                    <li key={s.snapshot_id} className="py-2 flex items-center justify-between text-xs font-mono">
                      <div>
                        <span className="font-medium">{s.period.toUpperCase()}</span>
                        <span className="ml-3 text-muted-foreground">{s.snapshot_id}</span>
                      </div>
                      <div className="text-muted-foreground text-right">
                        {new Date(s.created_at).toLocaleString()} · {s.recipients?.length || 0} recipients · <span className="uppercase tracking-widest text-[10px]">{s.delivery_status}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </VLKCard>
        </>
      )}

      <SendDialog open={showSend} onClose={() => setShowSend(false)} defaultPeriod={period} onSent={refetchSnaps} />
    </div>
  );
}

function SendDialog({ open, onClose, defaultPeriod, onSent }) {
  const [period, setPeriod] = useState(defaultPeriod);
  const [recipients, setRecipients] = useState("");
  const mut = useMutation({
    mutationFn: () => api.post("/digest/send", {
      period,
      recipients: recipients.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean),
    }).then((r) => r.data),
    onSuccess: () => { toast.success("Digest snapshot queued", { description: "Mocked delivery — see ADR-0032 for SMTP integration." }); onSent?.(); onClose(); },
    onError: (e) => toast.error("Send failed", { description: apiErrorText(e) }),
  });
  return (
    <VLKAlertDialog
      open={open}
      onOpenChange={(v) => !v && onClose()}
      title="Queue executive digest"
      actions={<>
        <VLKButton variant="ghost" onClick={onClose}>Cancel</VLKButton>
        <VLKButton data-testid="digest-send-submit" loading={mut.isPending} onClick={() => mut.mutate()}>
          Queue snapshot
        </VLKButton>
      </>}
    >
      <div className="space-y-3">
        <div>
          <VLKLabel>Period</VLKLabel>
          <VLKSelect value={period} onChange={(e) => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </VLKSelect>
        </div>
        <div>
          <VLKLabel>Recipients (comma-separated emails)</VLKLabel>
          <VLKInput data-testid="digest-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="exec@veralok.io, ceo@customer.gov" />
        </div>
        <div className="text-[11px] text-warning bg-warning/5 border border-warning/30 rounded-sm p-2">
          Live email delivery is currently mocked. This action snapshots the digest to the ledger and audit-logs the dispatch.
        </div>
      </div>
    </VLKAlertDialog>
  );
}
